import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSpinner,
  faPlus,
  faSave,
  faGripVertical,
  faChevronDown,
  faChevronRight,
  faPencil,
  faTrash,
  faCheckDouble,
  faArchive,
  faCopy,
  faCodeBranch,
  faClockRotateLeft,
  faEllipsisVertical,
  faInfoCircle,
  faXmark,
  faImage,
  faVideo,
  faFile,
} from '@fortawesome/free-solid-svg-icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { useHasPermission } from '@/hooks/use-permissions';
import { SkeletonPage } from '@/components/skeletons';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast, swalConfirm, confirmDelete } from '@/lib/swal';
import { cn } from '@/lib/cn';
import { useProcedureTemplate } from './hooks/useProcedureTemplates';
import {
  useCreateProcedure,
  useUpdateProcedure,
  usePublishProcedure,
  useArchiveProcedure,
  useNewVersionProcedure,
  useDeleteProcedure,
  useQuickEditProcedure,
} from './hooks/useProcedureMutations';
import {
  useCreateSection,
  useUpdateSection,
  useDeleteSection,
  useReorderSections,
} from './hooks/useSectionMutations';
import {
  useCreateStep,
  useUpdateStep,
  useDeleteStep,
  useReorderSteps,
  useUploadRefMaterial,
  useDeleteRefMaterial,
} from './hooks/useStepMutations';
import { ProcedureStatusBadge } from './components/ProcedureStatusBadge';
import { ProcedureTypeBadge } from './components/ProcedureTypeBadge';
import { RichTextEditor } from './components/RichTextEditor';
import { CloneDialog } from './components/CloneDialog';
import {
  EquipmentModelCombobox,
  type EquipmentModelOption,
} from '@/components/shared/EquipmentModelCombobox';
import { ROUTES } from '@/routes/routes';
import type {
  ProcedureTemplate,
  ProcedureSection,
  ProcedureStep,
  ProcedureType,
  MaterialType,
  UpdateStepDto,
} from '@/services/procedure-template.service';

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

// ── Step editor (inline, within section) ─────────────────────────────────────

interface StepEditorProps {
  templateId: string;
  sectionId: string;
  step: ProcedureStep;
  stepIndex: number;
  isDraft: boolean;
  isQuickEdit?: boolean;
  onQuickEditChange?: (
    stepId: string,
    changes: { title?: string; description?: string; instructionContent?: string },
  ) => void;
}

function StepEditorRow({
  templateId,
  sectionId,
  step,
  stepIndex,
  isDraft,
  isQuickEdit = false,
  onQuickEditChange,
}: StepEditorProps) {
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState(step.title);
  const [description, setDescription] = useState(step.description ?? '');
  const [instruction, setInstruction] = useState(step.instructionContent ?? '');
  const [qeTitle, setQeTitle] = useState(step.title);
  const [qeDescription, setQeDescription] = useState(step.description ?? '');
  const [qeInstruction, setQeInstruction] = useState(step.instructionContent ?? '');
  const [isMandatory, setIsMandatory] = useState(step.isMandatory);
  const [passFailRequired, setPassFailRequired] = useState(step.passFailRequired);
  const [authRequired, setAuthRequired] = useState(step.authorizationRequired);
  const [evidenceRequired, setEvidenceRequired] = useState(step.evidenceRequired);
  const [commentsAllowed, setCommentsAllowed] = useState(step.commentsAllowed);
  const [minPhotos, setMinPhotos] = useState(step.minPhotos);
  const [minVideos, setMinVideos] = useState(step.minVideos);
  const [minDocuments, setMinDocuments] = useState(step.minDocuments);
  const [uploadingType, setUploadingType] = useState<MaterialType | null>(null);

  const photoRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const docRef = useRef<HTMLInputElement>(null);

  const updateStep = useUpdateStep(templateId, sectionId);
  const deleteStep = useDeleteStep(templateId, sectionId);
  const uploadMat = useUploadRefMaterial(templateId, sectionId, step.id);
  const deleteMat = useDeleteRefMaterial(templateId, sectionId, step.id);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.id,
  });
  const style = { transform: CSS.Transform.toString(transform), transition };

  // Reset form when step data changes externally
  useEffect(() => {
    if (!expanded) {
      setTitle(step.title);
      setDescription(step.description ?? '');
      setInstruction(step.instructionContent ?? '');
      setIsMandatory(step.isMandatory);
      setPassFailRequired(step.passFailRequired);
      setAuthRequired(step.authorizationRequired);
      setEvidenceRequired(step.evidenceRequired);
      setCommentsAllowed(step.commentsAllowed);
      setMinPhotos(step.minPhotos);
      setMinVideos(step.minVideos);
      setMinDocuments(step.minDocuments);
      setQeTitle(step.title);
      setQeDescription(step.description ?? '');
      setQeInstruction(step.instructionContent ?? '');
    }
  }, [step, expanded]);

  // Interdependency: passFailRequired forces isMandatory
  const effectiveMandatory = isMandatory || passFailRequired || authRequired;

  async function handleSave() {
    if (title.trim().length < 3) {
      toast.error('Step title must be at least 3 characters');
      return;
    }
    if (evidenceRequired && minPhotos === 0 && minVideos === 0 && minDocuments === 0) {
      toast.error('At least one minimum evidence count must be ≥ 1 when evidence is required');
      return;
    }
    const dto: UpdateStepDto = {
      title: title.trim(),
      description: description.trim() || undefined,
      instructionContent: instruction ? DOMPurify.sanitize(instruction) : undefined,
      isMandatory: effectiveMandatory,
      passFailRequired,
      authorizationRequired: authRequired,
      evidenceRequired,
      commentsAllowed,
      minPhotos: evidenceRequired ? minPhotos : 0,
      minVideos: evidenceRequired ? minVideos : 0,
      minDocuments: evidenceRequired ? minDocuments : 0,
    };
    try {
      await updateStep.mutateAsync({ stepId: step.id, dto });
      toast.success('Step saved');
      setExpanded(false);
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to save step'));
    }
  }

  async function handleDelete() {
    const hasRefs = step.referenceMaterials.length > 0;
    const ok = await swalConfirm({
      title: `Delete step ${stepIndex + 1}: '${step.title}'?`,
      text: hasRefs
        ? `This step and its ${step.referenceMaterials.length} reference materials will be removed.`
        : 'This step will be permanently removed.',
    });
    if (!ok) return;
    try {
      await deleteStep.mutateAsync(step.id);
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to delete step'));
    }
  }

  async function handleUpload(file: File, materialType: MaterialType) {
    setUploadingType(materialType);
    try {
      await uploadMat.mutateAsync({ file, materialType });
      toast.success('Reference material uploaded');
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Upload failed'));
    } finally {
      setUploadingType(null);
    }
  }

  async function handleDeleteMat(matId: string) {
    const ok = await swalConfirm({
      title: 'Delete reference material?',
      text: 'This file will be removed.',
    });
    if (!ok) return;
    try {
      await deleteMat.mutateAsync(matId);
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to delete material'));
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('rounded-lg border border-border bg-card', isDragging && 'opacity-50 z-10')}
    >
      {/* Summary row */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        {isDraft && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="flex h-6 w-5 shrink-0 cursor-grab items-center justify-center text-muted-foreground/40 hover:text-muted-foreground active:cursor-grabbing"
            aria-label="Drag to reorder"
          >
            <FontAwesomeIcon icon={faGripVertical} className="h-3.5 w-3.5" />
          </button>
        )}
        <span className="text-xs font-semibold text-muted-foreground w-5 shrink-0">
          {stepIndex + 1}
        </span>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex flex-1 items-center gap-2 text-left"
        >
          <span className="flex-1 text-sm font-medium text-foreground line-clamp-1">
            {step.title}
          </span>
          <div className="flex items-center gap-1 shrink-0">
            {step.isMandatory && (
              <span className="rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                Mandatory
              </span>
            )}
            {step.authorizationRequired && (
              <span className="rounded-full bg-sky-500/10 px-1.5 py-0.5 text-[10px] font-medium text-sky-700">
                Auth
              </span>
            )}
            {step.evidenceRequired && (
              <span className="rounded-full bg-orange-500/10 px-1.5 py-0.5 text-[10px] font-medium text-orange-700">
                Evidence
              </span>
            )}
            {step.passFailRequired && (
              <span className="rounded-full bg-green-500/10 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
                Pass/Fail
              </span>
            )}
          </div>
          <FontAwesomeIcon
            icon={expanded ? faChevronDown : faChevronRight}
            className="h-3 w-3 text-muted-foreground"
          />
        </button>
        {isDraft && (
          <button
            type="button"
            onClick={() => void handleDelete()}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-red-500/10 hover:text-red-600 transition-colors"
          >
            <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Expanded edit form */}
      {expanded && isDraft && (
        <div className="border-t border-border px-4 pb-4 pt-3 space-y-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">
              Title <span className="text-destructive">*</span>
            </label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Short Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Instructions</label>
            <RichTextEditor
              value={instruction}
              onChange={setInstruction}
              placeholder="Add step instructions…"
            />
          </div>

          {/* Config toggles */}
          <div className="space-y-2 rounded-lg bg-muted/20 p-3">
            <p className="text-xs font-semibold text-foreground uppercase tracking-wide">
              Configuration
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {[
                {
                  label: 'Mandatory',
                  value: effectiveMandatory,
                  onChange: (v: boolean) => {
                    if (!passFailRequired && !authRequired) setIsMandatory(v);
                  },
                  locked: passFailRequired || authRequired,
                },
                {
                  label: 'Pass/Fail Required',
                  value: passFailRequired,
                  onChange: (v: boolean) => {
                    setPassFailRequired(v);
                    if (v) setIsMandatory(true);
                  },
                },
                {
                  label: 'Authorization Required',
                  value: authRequired,
                  onChange: (v: boolean) => {
                    setAuthRequired(v);
                    if (v) setIsMandatory(true);
                  },
                },
                {
                  label: 'Evidence Required',
                  value: evidenceRequired,
                  onChange: setEvidenceRequired,
                },
                { label: 'Comments Allowed', value: commentsAllowed, onChange: setCommentsAllowed },
              ].map(({ label, value, onChange, locked }) => (
                <label
                  key={label}
                  className={cn('flex items-center gap-2 cursor-pointer', locked && 'opacity-60')}
                >
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                    disabled={locked}
                    className="h-4 w-4 rounded border-border"
                  />
                  <span className="text-xs text-foreground">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Evidence limits */}
          {evidenceRequired && (
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Min Photos', value: minPhotos, onChange: setMinPhotos, max: 10 },
                { label: 'Min Videos', value: minVideos, onChange: setMinVideos, max: 2 },
                { label: 'Min Documents', value: minDocuments, onChange: setMinDocuments, max: 5 },
              ].map(({ label, value, onChange, max }) => (
                <div key={label} className="space-y-1">
                  <label className="text-xs font-medium text-foreground">{label}</label>
                  <input
                    type="number"
                    min={0}
                    max={max}
                    value={value}
                    onChange={(e) =>
                      onChange(Math.max(0, Math.min(max, parseInt(e.target.value, 10) || 0)))
                    }
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Reference materials */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-foreground uppercase tracking-wide">
                Reference Materials
              </p>
              <div className="flex items-center gap-1.5">
                {[
                  {
                    type: 'IMAGE' as MaterialType,
                    icon: faImage,
                    accept: 'image/jpeg,image/png,image/webp',
                    ref: photoRef,
                  },
                  {
                    type: 'VIDEO' as MaterialType,
                    icon: faVideo,
                    accept: 'video/mp4,video/quicktime,video/webm',
                    ref: videoRef,
                  },
                  {
                    type: 'DOCUMENT' as MaterialType,
                    icon: faFile,
                    accept: 'application/pdf,.docx,.xlsx',
                    ref: docRef,
                  },
                ].map(({ type, icon, accept, ref }) => (
                  <span key={type}>
                    <input
                      ref={ref}
                      type="file"
                      accept={accept}
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handleUpload(f, type);
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => ref.current?.click()}
                      disabled={uploadingType === type}
                      className="flex h-7 items-center gap-1 rounded-md border border-border px-2 text-xs text-foreground hover:bg-muted disabled:opacity-50 transition-colors"
                    >
                      {uploadingType === type ? (
                        <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
                      ) : (
                        <FontAwesomeIcon icon={icon} className="h-3 w-3" />
                      )}
                      <span className="hidden sm:inline">
                        {type === 'IMAGE' ? 'Image' : type === 'VIDEO' ? 'Video' : 'Doc'}
                      </span>
                    </button>
                  </span>
                ))}
              </div>
            </div>
            {step.referenceMaterials.length > 0 && (
              <div className="space-y-1.5">
                {step.referenceMaterials.map((m) => (
                  <div
                    key={m.id}
                    className="flex items-center justify-between rounded-lg border border-border bg-muted/20 px-3 py-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FontAwesomeIcon
                        icon={
                          m.materialType === 'IMAGE'
                            ? faImage
                            : m.materialType === 'VIDEO'
                              ? faVideo
                              : faFile
                        }
                        className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                      />
                      <a
                        href={m.signedUrl}
                        download={m.fileName}
                        target="_blank"
                        rel="noreferrer"
                        className="truncate text-xs text-foreground hover:text-primary hover:underline"
                      >
                        {m.fileName}
                      </a>
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleDeleteMat(m.id)}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-red-600 transition-colors"
                    >
                      <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Step actions */}
          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button
              variant="ghost"
              onClick={() => setExpanded(false)}
              disabled={updateStep.isPending}
            >
              Cancel
            </Button>
            <Button onClick={() => void handleSave()} disabled={updateStep.isPending}>
              {updateStep.isPending && (
                <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3 w-3 animate-spin" />
              )}
              Save Step
            </Button>
          </div>
        </div>
      )}

      {/* Quick-edit expanded form */}
      {expanded && isQuickEdit && (
        <div className="border-t border-border px-4 pb-4 pt-3 space-y-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">
              Title <span className="text-destructive">*</span>
            </label>
            <Input
              value={qeTitle}
              onChange={(e) => {
                const v = e.target.value;
                setQeTitle(v);
                onQuickEditChange?.(step.id, {
                  title: v,
                  description: qeDescription || undefined,
                  instructionContent: qeInstruction || undefined,
                });
              }}
              maxLength={300}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Short Description</label>
            <textarea
              value={qeDescription}
              onChange={(e) => {
                const v = e.target.value;
                setQeDescription(v);
                onQuickEditChange?.(step.id, {
                  title: qeTitle,
                  description: v || undefined,
                  instructionContent: qeInstruction || undefined,
                });
              }}
              rows={2}
              maxLength={500}
              className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Instructions</label>
            <RichTextEditor
              value={qeInstruction}
              onChange={(v) => {
                setQeInstruction(v);
                onQuickEditChange?.(step.id, {
                  title: qeTitle,
                  description: qeDescription || undefined,
                  instructionContent: v || undefined,
                });
              }}
              placeholder="Add step instructions…"
            />
          </div>
          <div className="flex justify-end border-t border-border pt-3">
            <Button variant="ghost" onClick={() => setExpanded(false)}>
              Close
            </Button>
          </div>
        </div>
      )}

      {/* Read-only expanded view (non-DRAFT, non-QuickEdit) */}
      {expanded && !isDraft && !isQuickEdit && (
        <div className="border-t border-border px-4 pb-4 pt-3 space-y-2">
          {step.description && <p className="text-sm text-muted-foreground">{step.description}</p>}
          {step.instructionContent && (
            <div
              className="prose prose-sm max-w-none text-foreground dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(step.instructionContent) }}
            />
          )}
          {step.referenceMaterials.length > 0 && (
            <div className="space-y-1">
              {step.referenceMaterials.map((m) => (
                <a
                  key={m.id}
                  href={m.signedUrl}
                  download={m.fileName}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-xs text-primary hover:underline"
                >
                  <FontAwesomeIcon
                    icon={
                      m.materialType === 'IMAGE'
                        ? faImage
                        : m.materialType === 'VIDEO'
                          ? faVideo
                          : faFile
                    }
                    className="h-3 w-3"
                  />
                  {m.fileName}
                </a>
              ))}
            </div>
          )}
          <Button variant="ghost" onClick={() => setExpanded(false)} className="mt-2">
            Close
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Section item (sortable) ───────────────────────────────────────────────────

interface SectionItemProps {
  template: ProcedureTemplate;
  section: ProcedureSection;
  sectionIndex: number;
  isDraft: boolean;
  isQuickEdit?: boolean;
  onQuickEditChange?: (
    stepId: string,
    changes: { title?: string; description?: string; instructionContent?: string },
  ) => void;
}

function SectionItem({
  template,
  section,
  sectionIndex,
  isDraft,
  isQuickEdit = false,
  onQuickEditChange,
}: SectionItemProps) {
  const [expanded, setExpanded] = useState(true);
  const [editingTitle, setEditingTitle] = useState(false);
  const [title, setTitle] = useState(section.title);
  const titleRef = useRef<HTMLInputElement>(null);

  const updateSection = useUpdateSection(template.id);
  const deleteSection = useDeleteSection(template.id);
  const createStep = useCreateStep(template.id, section.id);
  const reorderSteps = useReorderSteps(template.id, section.id);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
  });
  const style = { transform: CSS.Transform.toString(transform), transition };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    if (editingTitle) titleRef.current?.focus();
  }, [editingTitle]);

  async function handleTitleSave() {
    if (title.trim().length < 2) {
      setTitle(section.title);
      setEditingTitle(false);
      return;
    }
    if (title.trim() === section.title) {
      setEditingTitle(false);
      return;
    }
    try {
      await updateSection.mutateAsync({ sectionId: section.id, title: title.trim() });
      setEditingTitle(false);
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to update section'));
    }
  }

  async function handleDeleteSection() {
    const hasSteps = section.steps.length > 0;
    const ok = await swalConfirm({
      title: `Delete '${section.title}'?`,
      text: hasSteps
        ? `This section contains ${section.steps.length} steps. All steps and their reference materials will be permanently removed.`
        : 'This section will be permanently removed.',
    });
    if (!ok) return;
    try {
      await deleteSection.mutateAsync(section.id);
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to delete section'));
    }
  }

  async function handleAddStep() {
    try {
      await createStep.mutateAsync({ title: 'New Step', isMandatory: true, commentsAllowed: true });
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to add step'));
    }
  }

  function handleStepDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = section.steps.findIndex((s) => s.id === active.id);
    const newIdx = section.steps.findIndex((s) => s.id === over.id);
    const newOrder = arrayMove(section.steps, oldIdx, newIdx);
    void reorderSteps.mutateAsync(newOrder.map((s) => s.id));
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('rounded-xl border border-border bg-card', isDragging && 'opacity-50')}
    >
      {/* Section header */}
      <div className="flex items-center gap-2 px-3 py-3">
        {isDraft && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="flex h-7 w-5 shrink-0 cursor-grab items-center justify-center text-muted-foreground/40 hover:text-muted-foreground active:cursor-grabbing"
          >
            <FontAwesomeIcon icon={faGripVertical} className="h-4 w-4" />
          </button>
        )}

        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
          {sectionIndex + 1}
        </span>

        {editingTitle ? (
          <input
            ref={titleRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => void handleTitleSave()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleTitleSave();
              if (e.key === 'Escape') {
                setTitle(section.title);
                setEditingTitle(false);
              }
            }}
            className="flex-1 rounded-lg border border-ring bg-background px-2 py-1 text-sm font-semibold text-foreground focus:outline-none"
          />
        ) : (
          <button
            type="button"
            onClick={() => isDraft && setEditingTitle(true)}
            className={cn(
              'flex-1 text-left text-sm font-semibold text-foreground',
              isDraft && 'hover:text-primary cursor-pointer',
            )}
          >
            {section.title}
          </button>
        )}

        <span className="text-xs text-muted-foreground shrink-0">
          {section.steps.length} step{section.steps.length !== 1 ? 's' : ''}
        </span>

        {isDraft && (
          <button
            type="button"
            onClick={() => isDraft && setEditingTitle(true)}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted transition-colors"
          >
            <FontAwesomeIcon icon={faPencil} className="h-3.5 w-3.5" />
          </button>
        )}
        {isDraft && (
          <button
            type="button"
            onClick={() => void handleDeleteSection()}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-red-500/10 hover:text-red-600 transition-colors"
          >
            <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted transition-colors"
        >
          <FontAwesomeIcon
            icon={expanded ? faChevronDown : faChevronRight}
            className="h-3.5 w-3.5"
          />
        </button>
      </div>

      {/* Steps */}
      {expanded && (
        <div className="border-t border-border px-3 pb-3 pt-2 space-y-2">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleStepDragEnd}
          >
            <SortableContext
              items={section.steps.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              {section.steps.map((step, idx) => (
                <StepEditorRow
                  key={step.id}
                  templateId={template.id}
                  sectionId={section.id}
                  step={step}
                  stepIndex={idx}
                  isDraft={isDraft}
                  isQuickEdit={isQuickEdit}
                  onQuickEditChange={onQuickEditChange}
                />
              ))}
            </SortableContext>
          </DndContext>

          {section.steps.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">No steps added yet.</p>
          )}

          {isDraft && (
            <button
              type="button"
              onClick={() => void handleAddStep()}
              disabled={createStep.isPending}
              className="mt-1 flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors"
            >
              {createStep.isPending ? (
                <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
              )}
              Add Step
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Builder header overflow menu ──────────────────────────────────────────────

interface BuilderOverflowProps {
  template: ProcedureTemplate;
  can: (p: string) => boolean;
  onCloneClick: () => void;
}

function BuilderOverflowMenu({ template, can, onCloneClick }: BuilderOverflowProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const archiveMutation = useArchiveProcedure(template.id);
  const newVersionMutation = useNewVersionProcedure();
  const deleteMutation = useDeleteProcedure();

  const items = [
    {
      label: 'Clone',
      icon: faCopy,
      show: can('procedure:clone'),
      action: () => {
        onCloneClick();
      },
    },
    {
      label: 'Version History',
      icon: faClockRotateLeft,
      show: true,
      action: () => navigate(ROUTES.PROCEDURE_VERSIONS.replace(':id', template.id)),
    },
    {
      label: 'Archive',
      icon: faArchive,
      show: template.status === 'PUBLISHED' && can('procedure:archive'),
      action: async () => {
        const ok = await swalConfirm({
          title: `Archive '${template.title}'?`,
          text: 'This removes it from new inspection assignments.',
        });
        if (!ok) return;
        try {
          await archiveMutation.mutateAsync();
          toast.success('Archived');
          navigate(ROUTES.PROCEDURES);
        } catch (e) {
          toast.error(extractMsg(e as Error, 'Failed to archive'));
        }
      },
    },
    {
      label: 'New Version',
      icon: faCodeBranch,
      show: template.status === 'PUBLISHED' && can('procedure:clone'),
      action: async () => {
        const ok = await swalConfirm({
          title: 'Create New Version?',
          text: `A new draft v${template.versionNumber + 1} will be created from the published v${template.versionNumber}.`,
        });
        if (!ok) return;
        try {
          const result = await newVersionMutation.mutateAsync(template.id);
          toast.success(`Draft v${result.versionNumber} created`);
          navigate(ROUTES.PROCEDURE_EDIT.replace(':id', result.id));
        } catch (e) {
          toast.error(extractMsg(e as Error, 'Failed to create version'));
        }
      },
    },
    {
      label: 'Delete',
      icon: faTrash,
      show: template.status === 'DRAFT' && can('procedure:delete'),
      danger: true,
      action: async () => {
        const ok = await confirmDelete(template.title);
        if (!ok) return;
        try {
          await deleteMutation.mutateAsync(template.id);
          toast.success('Deleted');
          navigate(ROUTES.PROCEDURES);
        } catch (e) {
          toast.error(extractMsg(e as Error, 'Failed to delete'));
        }
      },
    },
  ];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
      >
        <FontAwesomeIcon icon={faEllipsisVertical} className="h-4 w-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 top-10 z-20 min-w-[160px] rounded-xl border border-border bg-card py-1 shadow-lg">
            {items
              .filter((i) => i.show)
              .map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    void item.action();
                  }}
                  className={cn(
                    'flex w-full items-center gap-2.5 px-4 py-2 text-sm transition-colors',
                    'danger' in item && item.danger
                      ? 'text-red-600 hover:bg-red-500/10'
                      : 'text-foreground hover:bg-muted',
                  )}
                >
                  <FontAwesomeIcon icon={item.icon} className="h-3.5 w-3.5 shrink-0" />
                  {item.label}
                </button>
              ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Main builder page ─────────────────────────────────────────────────────────

export function ProcedureBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const modeParam = searchParams.get('mode');
  const can = useHasPermission();
  const isCreateMode = !id;

  // Create mode local state
  const [createTitle, setCreateTitle] = useState('');
  const [createType, setCreateType] = useState<ProcedureType>('INSPECTION');
  const [createModel, setCreateModel] = useState<EquipmentModelOption | null>(null);
  const [createDescription, setCreateDescription] = useState('');
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);

  const { data: template, isLoading, error: loadError } = useProcedureTemplate(id);
  const createMutation = useCreateProcedure();
  const updateMutation = useUpdateProcedure(id ?? '');
  const publishMutation = usePublishProcedure(id ?? '');
  const quickEditMutation = useQuickEditProcedure(id ?? '');
  const createSection = useCreateSection(id ?? '');
  const reorderSections = useReorderSections(id ?? '');

  const [quickEditStepChanges, setQuickEditStepChanges] = useState<
    Record<string, { title?: string; description?: string; instructionContent?: string }>
  >({});

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Metadata edit state (edit mode)
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [metaModel, setMetaModel] = useState<EquipmentModelOption | null>(null);
  const [metaCustomerVisible, setMetaCustomerVisible] = useState(false);
  // Optional by default — a new procedure must not silently start blocking reports.
  const [metaIsMandatory, setMetaIsMandatory] = useState(false);
  const [metaDirty, setMetaDirty] = useState(false);

  // Sync metadata from loaded template
  useEffect(() => {
    if (template) {
      setMetaTitle(template.title);
      setMetaDescription(template.description ?? '');
      setMetaModel(
        template.equipmentModel
          ? {
              id: template.equipmentModel.id,
              modelNumber: template.equipmentModel.modelNumber,
              name: template.equipmentModel.name,
              manufacturerName: template.equipmentModel.manufacturer.name,
              equipmentTypeName: template.equipmentModel.equipmentTypeName,
            }
          : null,
      );
      setMetaCustomerVisible(template.customerVisible);
      setMetaIsMandatory(template.isMandatory);
      setMetaDirty(false);
    }
  }, [template]);

  // Redirect PUBLISHED/ARCHIVED to view (edit route guard), except when in quick-edit mode
  useEffect(() => {
    if (template && template.status !== 'DRAFT') {
      const isQE = modeParam === 'quick-edit' && template.status === 'PUBLISHED';
      if (!isQE) {
        navigate(ROUTES.PROCEDURE_VIEW.replace(':id', template.id), { replace: true });
      }
    }
  }, [template, navigate, modeParam]);

  if (!isCreateMode && isLoading) return <SkeletonPage />;
  if (!isCreateMode && loadError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-sm text-destructive">Failed to load procedure</p>
        <button
          onClick={() => navigate(ROUTES.PROCEDURES)}
          className="mt-3 text-sm text-primary hover:underline"
        >
          Back to Library
        </button>
      </div>
    );
  }

  const isDraft = isCreateMode || template?.status === 'DRAFT';
  const isQuickEdit = modeParam === 'quick-edit' && template?.status === 'PUBLISHED';

  function handleQuickEditStepChange(
    stepId: string,
    changes: { title?: string; description?: string; instructionContent?: string },
  ) {
    setQuickEditStepChanges((prev) => ({ ...prev, [stepId]: changes }));
  }

  async function handleSaveQuickEdit() {
    if (!template) return;
    try {
      const steps = Object.entries(quickEditStepChanges).map(([stepId, changes]) => ({
        id: stepId,
        ...changes,
      }));
      await quickEditMutation.mutateAsync({
        title: metaTitle.trim() || undefined,
        description: metaDescription.trim() || undefined,
        steps: steps.length > 0 ? steps : undefined,
      });
      setMetaDirty(false);
      setQuickEditStepChanges({});
      toast.success('Procedure updated');
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to save quick edit'));
    }
  }

  // ── Create mode submit ────────────────────────────────────────────────────
  async function handleCreate() {
    if (createTitle.trim().length < 3) {
      toast.error('Title must be at least 3 characters');
      return;
    }
    if (!createModel) {
      toast.error('Equipment model is required');
      return;
    }
    try {
      const result = await createMutation.mutateAsync({
        title: createTitle.trim(),
        description: createDescription.trim() || undefined,
        procedureType: createType,
        equipmentModelId: createModel.id,
      });
      toast.success('Procedure created');
      navigate(ROUTES.PROCEDURE_EDIT.replace(':id', result.id));
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to create procedure'));
    }
  }

  // ── Metadata save (edit mode) ─────────────────────────────────────────────
  async function handleSaveMeta() {
    if (!template) return;
    if (metaTitle.trim().length < 3) {
      toast.error('Title must be at least 3 characters');
      return;
    }
    try {
      await updateMutation.mutateAsync({
        title: metaTitle.trim(),
        description: metaDescription.trim() || undefined,
        equipmentModelId: metaModel?.id,
        customerVisible: metaCustomerVisible,
        isMandatory: metaIsMandatory,
      });
      setMetaDirty(false);
      toast.success('Saved');
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to save'));
    }
  }

  // ── Publish ───────────────────────────────────────────────────────────────
  async function handlePublish() {
    if (!template) return;
    const ok = await swalConfirm({
      title: `Publish '${template.title} v${template.versionNumber}'?`,
      html: `<p class="text-sm">Publishing will make this the active procedure for new inspections and lock this version from further editing.</p>`,
    });
    if (!ok) return;
    try {
      await publishMutation.mutateAsync();
      toast.success('Procedure published');
      navigate(ROUTES.PROCEDURE_VIEW.replace(':id', template.id));
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to publish'));
    }
  }

  // ── Section drag-end ──────────────────────────────────────────────────────
  function handleSectionDragEnd(event: DragEndEvent) {
    if (!template) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = template.sections.findIndex((s) => s.id === active.id);
    const newIdx = template.sections.findIndex((s) => s.id === over.id);
    const newOrder = arrayMove(template.sections, oldIdx, newIdx);
    void reorderSections.mutateAsync(newOrder.map((s) => s.id));
  }

  // ── Add section ───────────────────────────────────────────────────────────
  async function handleAddSection() {
    if (!template) return;
    try {
      await createSection.mutateAsync({ title: 'New Section' });
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to add section'));
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CREATE MODE render
  // ─────────────────────────────────────────────────────────────────────────
  if (isCreateMode) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(ROUTES.PROCEDURES)}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
          </button>
          <h1 className="text-xl font-semibold text-foreground">New Procedure</h1>
        </div>

        <div className="rounded-xl border border-border bg-card p-6 space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">
              Procedure Title <span className="text-destructive">*</span>
            </label>
            <Input
              value={createTitle}
              onChange={(e) => setCreateTitle(e.target.value)}
              placeholder="e.g. GA22 5-Year Inspection"
              maxLength={300}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">
              Procedure Type <span className="text-destructive">*</span>
            </label>
            <select
              value={createType}
              onChange={(e) => setCreateType(e.target.value as ProcedureType)}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="INSPECTION">Inspection</option>
              <option value="SERVICE">Service</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">
              Equipment Model <span className="text-destructive">*</span>
            </label>
            <EquipmentModelCombobox value={createModel} onChange={setCreateModel} />
            {!createModel && (
              <p className="text-xs text-muted-foreground">
                Search and select the equipment model this procedure applies to
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Description</label>
            <textarea
              value={createDescription}
              onChange={(e) => setCreateDescription(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Optional description of this procedure…"
              className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => navigate(ROUTES.PROCEDURES)}>
              Cancel
            </Button>
            <Button onClick={() => void handleCreate()} disabled={createMutation.isPending}>
              {createMutation.isPending && (
                <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3 w-3 animate-spin" />
              )}
              Create Procedure
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!template) return null;

  // ─────────────────────────────────────────────────────────────────────────
  // EDIT MODE render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-0">
      {/* Sticky header */}
      <div className="sticky top-0 z-30 flex items-center gap-3 border-b border-border bg-background/95 px-0 py-3 backdrop-blur-sm">
        <button
          type="button"
          onClick={() => navigate(ROUTES.PROCEDURES)}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <h1 className="truncate text-base font-semibold text-foreground">{template.title}</h1>
          <span className="shrink-0 text-xs text-muted-foreground">v{template.versionNumber}</span>
          <ProcedureStatusBadge status={template.status} className="shrink-0" />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {isQuickEdit ? (
            <>
              <Button
                variant="ghost"
                onClick={() => navigate(ROUTES.PROCEDURE_VIEW.replace(':id', template.id))}
                className="h-9"
              >
                Cancel
              </Button>
              <Button
                onClick={() => void handleSaveQuickEdit()}
                disabled={quickEditMutation.isPending}
                className="h-9"
              >
                {quickEditMutation.isPending && (
                  <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
                )}
                Save Quick Edit
              </Button>
            </>
          ) : (
            <>
              {isDraft && metaDirty && (
                <Button
                  onClick={() => void handleSaveMeta()}
                  disabled={updateMutation.isPending}
                  variant="ghost"
                  className="h-9"
                >
                  {updateMutation.isPending ? (
                    <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <FontAwesomeIcon icon={faSave} className="h-3.5 w-3.5" />
                  )}
                  <span className="ml-2 hidden sm:inline">Save</span>
                </Button>
              )}
              {isDraft && can('procedure:publish') && (
                <Button
                  onClick={() => void handlePublish()}
                  disabled={publishMutation.isPending || template.sections.length === 0}
                  className="h-9"
                >
                  {publishMutation.isPending ? (
                    <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <FontAwesomeIcon icon={faCheckDouble} className="mr-2 h-3.5 w-3.5" />
                  )}
                  Publish
                </Button>
              )}
              <BuilderOverflowMenu
                template={template}
                can={can}
                onCloneClick={() => setCloneDialogOpen(true)}
              />
            </>
          )}
        </div>
      </div>

      {/* Two-column layout */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
        {/* Left: metadata panel */}
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-foreground">Procedure Settings</p>
              <div className="flex items-center gap-1.5">
                <ProcedureTypeBadge procedureType={template.procedureType} />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Title <span className="text-destructive">*</span>
              </label>
              <Input
                value={metaTitle}
                onChange={(e) => {
                  setMetaTitle(e.target.value);
                  setMetaDirty(true);
                }}
                disabled={!isDraft && !isQuickEdit}
                maxLength={300}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Description</label>
              <textarea
                value={metaDescription}
                onChange={(e) => {
                  setMetaDescription(e.target.value);
                  setMetaDirty(true);
                }}
                rows={3}
                maxLength={1000}
                disabled={!isDraft && !isQuickEdit}
                className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Equipment Model</label>
              <EquipmentModelCombobox
                value={metaModel}
                onChange={(m) => {
                  setMetaModel(m);
                  setMetaDirty(true);
                }}
                disabled={!isDraft}
              />
            </div>

            <label className={cn('flex items-center gap-2', !isDraft && 'opacity-50')}>
              <input
                type="checkbox"
                checked={metaCustomerVisible}
                onChange={(e) => {
                  setMetaCustomerVisible(e.target.checked);
                  setMetaDirty(true);
                }}
                disabled={!isDraft}
                className="h-4 w-4 rounded border-border"
              />
              <span className="text-sm text-foreground">Visible in Customer Portal</span>
            </label>

            {/* Phase 5E — PROCEDURE-level policy. Deliberately placed apart from
                the per-step "Mandatory" toggle in the step editor, and worded to
                say what each one actually controls, because the two were being
                read as the same setting. */}
            <label className={cn('flex items-start gap-2', !isDraft && 'opacity-50')}>
              <input
                type="checkbox"
                checked={metaIsMandatory}
                onChange={(e) => {
                  setMetaIsMandatory(e.target.checked);
                  setMetaDirty(true);
                }}
                disabled={!isDraft}
                className="mt-0.5 h-4 w-4 rounded border-border"
              />
              <span className="text-sm text-foreground">
                Mandatory procedure
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  Mandatory procedures must be completed before an inspection report can be
                  generated. Optional procedures never block the report.
                </span>
                <span className="mt-0.5 block text-xs italic text-muted-foreground">
                  This is not the same as a step&apos;s Mandatory flag: a mandatory STEP must be
                  actioned before this procedure can be marked complete, but it never decides report
                  eligibility on its own.
                </span>
              </span>
            </label>

            {isDraft && metaDirty && (
              <Button
                onClick={() => void handleSaveMeta()}
                disabled={updateMutation.isPending}
                className="w-full"
              >
                {updateMutation.isPending && (
                  <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
                )}
                Save Changes
              </Button>
            )}
          </div>

          {/* Version info */}
          <div className="rounded-xl border border-border bg-card p-4 space-y-2 text-sm">
            <p className="font-semibold text-foreground">Version Info</p>
            <div className="space-y-1.5 text-muted-foreground">
              <div className="flex justify-between">
                <span>Version</span>
                <span className="text-foreground">v{template.versionNumber}</span>
              </div>
              <div className="flex justify-between">
                <span>Status</span>
                <ProcedureStatusBadge status={template.status} />
              </div>
              <div className="flex justify-between">
                <span>Created by</span>
                <span className="text-foreground truncate max-w-[120px]">
                  {template.createdBy.fullName}
                </span>
              </div>
              {template.publishedAt && (
                <div className="flex justify-between">
                  <span>Published</span>
                  <span className="text-foreground">
                    {new Date(template.publishedAt).toLocaleDateString()}
                  </span>
                </div>
              )}
              {template.previousVersionId && (
                <div className="flex justify-between">
                  <span>Previous</span>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(ROUTES.PROCEDURE_VIEW.replace(':id', template.previousVersionId!))
                    }
                    className="text-primary hover:underline"
                  >
                    View
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: sections + steps */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">Sections</p>
            {isDraft && (
              <Button
                variant="ghost"
                onClick={() => void handleAddSection()}
                disabled={createSection.isPending}
                className="h-9"
              >
                {createSection.isPending ? (
                  <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faPlus} className="mr-2 h-3.5 w-3.5" />
                )}
                Add Section
              </Button>
            )}
          </div>

          {template.sections.length === 0 && (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-16 text-center">
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="mb-3 h-8 w-8 text-muted-foreground/40"
              />
              <p className="text-sm text-muted-foreground">
                No sections yet. Add a section to start building.
              </p>
            </div>
          )}

          {isDraft ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleSectionDragEnd}
            >
              <SortableContext
                items={template.sections.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {template.sections.map((section, idx) => (
                    <SectionItem
                      key={section.id}
                      template={template}
                      section={section}
                      sectionIndex={idx}
                      isDraft={isDraft}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <div className="space-y-3">
              {template.sections.map((section, idx) => (
                <SectionItem
                  key={section.id}
                  template={template}
                  section={section}
                  sectionIndex={idx}
                  isDraft={false}
                  isQuickEdit={isQuickEdit}
                  onQuickEditChange={handleQuickEditStepChange}
                />
              ))}
            </div>
          )}

          {isQuickEdit && (
            <div className="flex items-start gap-2 rounded-lg border border-blue-300/40 bg-blue-500/5 px-3 py-2.5">
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-600"
              />
              <p className="text-xs text-blue-700">
                Quick Edit mode — you can update procedure title, description, and step text.
                Structural changes (sections, steps, flags) require creating a New Version.
              </p>
            </div>
          )}
          {!isDraft && !isQuickEdit && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300/40 bg-amber-500/5 px-3 py-2.5">
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600"
              />
              <p className="text-xs text-amber-700">
                {template.status === 'PUBLISHED'
                  ? 'This procedure is published and cannot be edited. To make changes, create a new version.'
                  : 'This version is archived. Create a new version to build on it.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {cloneDialogOpen && (
        <CloneDialog
          source={{ id: template.id, title: template.title, procedureType: template.procedureType }}
          onClose={() => setCloneDialogOpen(false)}
        />
      )}
    </div>
  );
}
